// Weapons: data-driven defs for all six element weapons + a runtime system that
// fires them by archetype. Resolved per-level stats are cached on each instance and
// only recomputed when the weapon levels up; passive Modifiers are applied inline so
// the per-frame path allocates nothing. Damage flows through combat.dealDamage (crit +
// splash). See plan §3.2 / §3.3.

import { Pool } from '../core/pool';
import { MAX_ENEMY_RADIUS, POOL_CAP, VIEW } from './balance';
import { dealDamage } from './combat';
import type { HitCtx } from './combat';
import { spawnBeam } from './fx';
import type { Enemy } from './enemies';

export type Element = 'acid' | 'fire' | 'frost' | 'spark' | 'poison' | 'arcane';

export const ELEMENT_COLOR: Record<Element, number> = {
  acid: 0x7cfc00,
  fire: 0xff6b1a,
  frost: 0x4fd6ff,
  spark: 0xffe14d,
  poison: 0xb14dff,
  arcane: 0xff4dff,
};

export type WeaponArchetype = 'lob' | 'pierce' | 'cone' | 'chain' | 'aura' | 'orbit';

export interface WeaponStats {
  damage: number;
  cooldownSec: number;
  count: number;
  speed: number;
  radius: number;
  range: number;
  lifeSec: number;
  pierce: number;
  slowMul: number;
  slowSec: number;
  burnDps: number;
  burnSec: number;
  coneHalfAngle: number;
  tickSec: number;
  angularSpeed: number;
  /** On-expiry explosion for slow drifting projectiles (0 = no explosion). */
  burstDamage: number;
  burstRadius: number;
  /** Permanent terrain interaction used by The Starfall Conduit. */
  terrainBounces: number;
  splitCount: number;
  splitDamage: number;
  /** Aurora Arc's post-ricochet links. */
  bounceChainCount: number;
  bounceChainDamage: number;
  bounceChainRange: number;
}

const mk = (o: Partial<WeaponStats>): WeaponStats => ({
  damage: 0,
  cooldownSec: 1,
  count: 1,
  speed: 0,
  radius: 4,
  range: 0,
  lifeSec: 0,
  pierce: 0,
  slowMul: 1,
  slowSec: 0,
  burnDps: 0,
  burnSec: 0,
  coneHalfAngle: 0,
  tickSec: 0,
  angularSpeed: 0,
  burstDamage: 0,
  burstRadius: 0,
  terrainBounces: 0,
  splitCount: 0,
  splitDamage: 0,
  bounceChainCount: 0,
  bounceChainDamage: 0,
  bounceChainRange: 0,
  ...o,
});

export interface WeaponDef {
  key: string;
  name: string;
  element: Element;
  archetype: WeaponArchetype;
  maxLevel: number;
  desc: string;
  stats: (level: number) => WeaponStats;
  /** Optional explicit color (reactions use custom colors; else element color). */
  color?: number;
  /** Optional projectile sprite key (defaults to 'proj'); the hidden variant uses this. */
  projSprite?: string;
}

export interface OrbitVisual {
  weaponKey: string;
  count: number;
  ringRadius: number;
  orbRadius: number;
  color: number;
  spin: number;
}

const defColor = (def: WeaponDef): number => def.color ?? ELEMENT_COLOR[def.element];

// Apply a weapon's slow/burn to an enemy (used by aura/orbit; projectiles carry their own).
function applyStatus(e: Enemy, s: WeaponStats): void {
  if (s.slowSec > 0) {
    e.slowSec = Math.max(e.slowSec, s.slowSec);
    e.slowMul = Math.min(e.slowMul, s.slowMul);
  }
  if (s.burnSec > 0) {
    e.burnSec = Math.max(e.burnSec, s.burnSec);
    e.burnDps = Math.max(e.burnDps, s.burnDps);
  }
}

export const WEAPON_DEFS: Record<string, WeaponDef> = {
  acid: {
    key: 'acid',
    name: 'Acid Vial',
    element: 'acid',
    archetype: 'lob',
    maxLevel: 8,
    desc: 'Lobs a corrosive vial at the nearest foe.',
    stats: (l) =>
      mk({
        damage: 8 + l * 4,
        cooldownSec: Math.max(0.35, 1.15 - l * 0.07),
        count: 1 + Math.floor(l / 3),
        speed: 340,
        radius: 4 + l * 0.3,
        range: 480,
        lifeSec: 1.4,
      }),
  },
  fire: {
    key: 'fire',
    name: 'Fire Bellows',
    element: 'fire',
    archetype: 'cone',
    maxLevel: 8,
    desc: 'Sprays a cone of flame that burns over time.',
    stats: (l) =>
      mk({
        damage: 4 + l * 2,
        cooldownSec: Math.max(0.5, 1.2 - l * 0.06),
        count: 4 + l,
        speed: 300,
        radius: 5,
        range: 190,
        lifeSec: 0.32,
        coneHalfAngle: 0.5,
        burnDps: 4 + l * 1.5,
        burnSec: 2,
      }),
  },
  frost: {
    key: 'frost',
    name: 'Frost Shard',
    element: 'frost',
    archetype: 'pierce',
    maxLevel: 8,
    desc: 'Piercing shard that chills (slows) enemies.',
    stats: (l) =>
      mk({
        damage: 8 + l * 3,
        cooldownSec: Math.max(0.5, 1.3 - l * 0.07),
        count: 1 + Math.floor(l / 4),
        speed: 430,
        radius: 5,
        range: 520,
        lifeSec: 1.1,
        pierce: 1 + l,
        slowMul: 0.6,
        slowSec: 1.2,
      }),
  },
  spark: {
    key: 'spark',
    name: 'Lightning Coil',
    element: 'spark',
    archetype: 'chain',
    maxLevel: 8,
    desc: 'Zaps the nearest foe and chains to others.',
    stats: (l) =>
      mk({
        damage: 7 + l * 3,
        cooldownSec: Math.max(0.45, 1.1 - l * 0.06),
        count: 2 + l,
        range: 300,
      }),
  },
  poison: {
    key: 'poison',
    name: 'Poison Miasma',
    element: 'poison',
    archetype: 'aura',
    maxLevel: 8,
    desc: 'A toxic cloud that damages all around you.',
    stats: (l) =>
      mk({
        damage: 3 + l * 1.5,
        radius: 68 + l * 8,
        tickSec: 0.5,
      }),
  },
  arcane: {
    key: 'arcane',
    name: 'Transmutation Ring',
    element: 'arcane',
    archetype: 'orbit',
    maxLevel: 8,
    desc: 'Orbs that orbit you, smashing foes they touch.',
    stats: (l) =>
      mk({
        damage: 6 + l * 2.5,
        count: 2 + Math.floor(l / 2),
        radius: 9,
        range: 68 + l * 4,
        angularSpeed: 2.4,
      }),
  },

  // --- Reactions (fusions; unlocked via the draft when both ingredients are leveled) ---
  steam: {
    key: 'steam', name: 'Steam Burst', element: 'frost', archetype: 'aura', maxLevel: 5, color: 0xcfeeff,
    desc: 'Fire + Frost: scalding cloud that damages and slows.',
    stats: (l) => mk({ damage: 10 + l * 4, radius: 95 + l * 12, tickSec: 0.4, slowMul: 0.55, slowSec: 1.0 }),
  },
  caustic: {
    key: 'caustic', name: 'Caustic Arc', element: 'spark', archetype: 'chain', maxLevel: 5, color: 0xd6ff5a,
    desc: 'Acid + Spark: lightning that chains far and hits hard.',
    stats: (l) => mk({ damage: 14 + l * 5, cooldownSec: Math.max(0.4, 0.9 - l * 0.05), count: 4 + l, range: 340 }),
  },
  napalm: {
    key: 'napalm', name: 'Napalm', element: 'fire', archetype: 'aura', maxLevel: 5, color: 0xff8a2a,
    desc: 'Fire + Poison: a burning cloud that ignites enemies.',
    stats: (l) => mk({ damage: 6 + l * 3, radius: 90 + l * 10, tickSec: 0.45, burnDps: 10 + l * 3, burnSec: 2 }),
  },
  shatter: {
    key: 'shatter', name: 'Shatter Orbit', element: 'arcane', archetype: 'orbit', maxLevel: 5, color: 0xb0e0ff,
    desc: 'Frost + Arcane: orbs that freeze and smash.',
    stats: (l) => mk({ damage: 12 + l * 5, count: 4 + l, radius: 11, range: 80 + l * 5, angularSpeed: 2.8, slowMul: 0.6, slowSec: 0.8 }),
  },
  storm: {
    key: 'storm', name: 'Storm Halo', element: 'spark', archetype: 'orbit', maxLevel: 5, color: 0xfff06a,
    desc: 'Spark + Arcane: orbiting tesla nodes.',
    stats: (l) => mk({ damage: 14 + l * 6, count: 3 + l, radius: 12, range: 90 + l * 5, angularSpeed: 3.2 }),
  },
  vitriol: {
    key: 'vitriol', name: 'Vitriol', element: 'acid', archetype: 'lob', maxLevel: 5, color: 0x9affc0,
    desc: 'Acid + Frost: corrosive bombs that leave slowing pools.',
    stats: (l) =>
      mk({ damage: 16 + l * 6, cooldownSec: Math.max(0.4, 1.0 - l * 0.06), count: 2 + Math.floor(l / 2), speed: 320, radius: 7, range: 500, lifeSec: 1.4, slowMul: 0.6, slowSec: 1.2 }),
  },
  // --- The Verdant Still: six botanical brews and six native reactions ---
  sapFlask: {
    key: 'sapFlask', name: 'Sap Flask', element: 'acid', archetype: 'lob', maxLevel: 8, color: 0xb9e84f,
    desc: 'Lobs sticky sap globes into the closest cluster.', projSprite: 'projectileSap',
    stats: (l) => mk({ damage: 8 + l * 4, cooldownSec: Math.max(0.36, 1.16 - l * 0.07), count: 1 + Math.floor(l / 3), speed: 325, radius: 5 + l * 0.25, range: 470, lifeSec: 1.45 }),
  },
  cinderHops: {
    key: 'cinderHops', name: 'Cinder Hops', element: 'fire', archetype: 'cone', maxLevel: 8, color: 0xff9b45,
    desc: 'Scatters burning hop cones that smolder on impact.', projSprite: 'projectileHop',
    stats: (l) => mk({ damage: 4 + l * 2, cooldownSec: Math.max(0.5, 1.2 - l * 0.06), count: 4 + l, speed: 285, radius: 5, range: 185, lifeSec: 0.34, coneHalfAngle: 0.54, burnDps: 4 + l * 1.5, burnSec: 2 }),
  },
  thornNeedle: {
    key: 'thornNeedle', name: 'Thorn Needle', element: 'frost', archetype: 'pierce', maxLevel: 8, color: 0x75c95d,
    desc: 'Fires a rigid briar spine through a line of creatures.', projSprite: 'projectileThorn',
    stats: (l) => mk({ damage: 9 + l * 3, cooldownSec: Math.max(0.5, 1.3 - l * 0.07), count: 1 + Math.floor(l / 4), speed: 445, radius: 4, range: 540, lifeSec: 1.15, pierce: 1 + l }),
  },
  fireflyJar: {
    key: 'fireflyJar', name: 'Firefly Jar', element: 'spark', archetype: 'chain', maxLevel: 8, color: 0xffdf78,
    desc: 'Releases hungry fireflies that leap between living targets.',
    stats: (l) => mk({ damage: 7 + l * 3, cooldownSec: Math.max(0.45, 1.1 - l * 0.06), count: 2 + l, range: 305 }),
  },
  sporeShroud: {
    key: 'sporeShroud', name: 'Spore Shroud', element: 'poison', archetype: 'aura', maxLevel: 8, color: 0xd680d8,
    desc: 'A breathing fungal veil damages everything around you.',
    stats: (l) => mk({ damage: 3 + l * 1.5, radius: 70 + l * 8, tickSec: 0.5 }),
  },
  seedHalo: {
    key: 'seedHalo', name: 'Seed Halo', element: 'arcane', archetype: 'orbit', maxLevel: 8, color: 0xa6f2dc,
    desc: 'Armored seed pods orbit the Brewmancer and batter the horde.',
    stats: (l) => mk({ damage: 6 + l * 2.5, count: 2 + Math.floor(l / 2), radius: 9, range: 68 + l * 4, angularSpeed: 2.4 }),
  },
  boilingWort: {
    key: 'boilingWort', name: 'Boiling Wort', element: 'fire', archetype: 'aura', maxLevel: 5, color: 0xffbd72,
    desc: 'Cinder Hops + Thorn Needle: a scalding mash circles the brewer.',
    stats: (l) => mk({ damage: 10 + l * 4, radius: 95 + l * 12, tickSec: 0.4, burnDps: 5 + l * 2, burnSec: 1.4 }),
  },
  thunderSap: {
    key: 'thunderSap', name: 'Thunder Sap', element: 'spark', archetype: 'chain', maxLevel: 5, color: 0xd7f45f,
    desc: 'Sap Flask + Firefly Jar: charged resin jumps through dense packs.',
    stats: (l) => mk({ damage: 14 + l * 5, cooldownSec: Math.max(0.4, 0.9 - l * 0.05), count: 4 + l, range: 345 }),
  },
  smolderMold: {
    key: 'smolderMold', name: 'Smolder Mold', element: 'poison', archetype: 'aura', maxLevel: 5, color: 0xe3789f,
    desc: 'Cinder Hops + Spore Shroud: hot spores ignite everything nearby.',
    stats: (l) => mk({ damage: 6 + l * 3, radius: 90 + l * 10, tickSec: 0.45, burnDps: 10 + l * 3, burnSec: 2 }),
  },
  thornCrown: {
    key: 'thornCrown', name: 'Thorn Crown', element: 'arcane', archetype: 'orbit', maxLevel: 5, color: 0x80d36b,
    desc: 'Thorn Needle + Seed Halo: a fast crown of piercing burrs.',
    stats: (l) => mk({ damage: 12 + l * 5, count: 4 + l, radius: 11, range: 80 + l * 5, angularSpeed: 2.8 }),
  },
  fireflyHalo: {
    key: 'fireflyHalo', name: 'Firefly Halo', element: 'spark', archetype: 'orbit', maxLevel: 5, color: 0xf5f07a,
    desc: 'Firefly Jar + Seed Halo: luminous beetles orbit in a biting swarm.',
    stats: (l) => mk({ damage: 14 + l * 6, count: 3 + l, radius: 12, range: 90 + l * 5, angularSpeed: 3.2 }),
  },
  nectarCatalyst: {
    key: 'nectarCatalyst', name: 'Nectar Catalyst', element: 'acid', archetype: 'aura', maxLevel: 5, color: 0xffc35a,
    desc: 'Sap Flask + Spore Shroud: nectar becomes short-lived amplifying pools.',
    stats: (l) => mk({ damage: 7 + l * 3, radius: 78 + l * 9, tickSec: 0.45 }),
  },

  // --- The Starfall Conduit: six astral mechanisms and six native reactions ---
  prismScepter: {
    key: 'prismScepter', name: 'Prism Scepter', element: 'arcane', archetype: 'lob', maxLevel: 8, color: 0x7fe7ff,
    desc: 'A crystal bolt ricochets from permanent terrain, then breaks into smaller shards.', projSprite: 'prismBolt',
    stats: (l) => mk({
      damage: 10 + l * 3.5, cooldownSec: Math.max(0.55, 1.16 - l * 0.06), count: 1 + Math.floor(l / 4),
      speed: 300, radius: 5, range: 500, lifeSec: 1.42, terrainBounces: 1,
      splitCount: 2 + Math.floor(l / 3), splitDamage: 5 + l * 2.2,
    }),
  },
  solarFan: {
    key: 'solarFan', name: 'Solar Fan', element: 'fire', archetype: 'cone', maxLevel: 8, color: 0xff8a45,
    desc: 'Projects a fan of incandescent stellar fragments.', projSprite: 'starShot',
    stats: (l) => mk({ damage: 4 + l * 2, cooldownSec: Math.max(0.5, 1.18 - l * 0.06), count: 4 + l, speed: 315, radius: 5, range: 200, lifeSec: 0.34, coneHalfAngle: 0.52, burnDps: 4 + l * 1.5, burnSec: 2 }),
  },
  cometLance: {
    key: 'cometLance', name: 'Comet Lance', element: 'frost', archetype: 'pierce', maxLevel: 8, color: 0xb2cdff,
    desc: 'Launches a cold iron comet through multiple constructs.', projSprite: 'projectileComet',
    stats: (l) => mk({ damage: 9 + l * 3, cooldownSec: Math.max(0.5, 1.28 - l * 0.07), count: 1 + Math.floor(l / 4), speed: 455, radius: 5, range: 550, lifeSec: 1.18, pierce: 1 + l, slowMul: 0.68, slowSec: 0.9 }),
  },
  arcRelay: {
    key: 'arcRelay', name: 'Arc Relay', element: 'spark', archetype: 'chain', maxLevel: 8, color: 0x74edff,
    desc: 'Routes conduit current through successive mechanical targets.',
    stats: (l) => mk({ damage: 7 + l * 3, cooldownSec: Math.max(0.45, 1.08 - l * 0.06), count: 2 + l, range: 315 }),
  },
  gravityWell: {
    key: 'gravityWell', name: 'Gravity Well', element: 'poison', archetype: 'aura', maxLevel: 8, color: 0x5965c8,
    desc: 'A compact distortion crushes nearby mechanisms.',
    stats: (l) => mk({ damage: 3 + l * 1.5, radius: 72 + l * 8, tickSec: 0.5, slowMul: 0.82, slowSec: 0.6 }),
  },
  moonletArray: {
    key: 'moonletArray', name: 'Moonlet Array', element: 'arcane', archetype: 'orbit', maxLevel: 8, color: 0xd5d8de,
    desc: 'Machined satellites orbit the Brewmancer and strike anything close.',
    stats: (l) => mk({ damage: 6 + l * 2.5, count: 2 + Math.floor(l / 2), radius: 9, range: 70 + l * 4, angularSpeed: 2.5 }),
  },
  coronaMist: {
    key: 'coronaMist', name: 'Corona Mist', element: 'fire', archetype: 'aura', maxLevel: 5, color: 0xffb36a,
    desc: 'Solar Fan + Comet Lance: a searing corona slows and burns nearby machines.',
    stats: (l) => mk({ damage: 10 + l * 4, radius: 98 + l * 12, tickSec: 0.4, slowMul: 0.65, slowSec: 0.8, burnDps: 5 + l * 2, burnSec: 1.3 }),
  },
  auroraArc: {
    key: 'auroraArc', name: 'Aurora Arc', element: 'spark', archetype: 'lob', maxLevel: 5, color: 0x92f8ff,
    desc: 'Prism Scepter + Arc Relay: a ricochet releases chain-lightning shards.', projSprite: 'prismBolt',
    stats: (l) => mk({
      damage: 15 + l * 4.5, cooldownSec: Math.max(0.48, 1.0 - l * 0.06), count: 1 + Math.floor(l / 3),
      speed: 330, radius: 6, range: 530, lifeSec: 1.48, terrainBounces: 1,
      splitCount: 3 + Math.floor(l / 2), splitDamage: 7 + l * 2.8,
      bounceChainCount: 2 + Math.floor(l / 2), bounceChainDamage: 8 + l * 3.2, bounceChainRange: 150,
    }),
  },
  redGiant: {
    key: 'redGiant', name: 'Red Giant', element: 'fire', archetype: 'aura', maxLevel: 5, color: 0xf05b46,
    desc: 'Solar Fan + Gravity Well: compressed heat burns through the surrounding field.',
    stats: (l) => mk({ damage: 7 + l * 3, radius: 92 + l * 10, tickSec: 0.44, burnDps: 11 + l * 3, burnSec: 2 }),
  },
  cometCrown: {
    key: 'cometCrown', name: 'Comet Crown', element: 'arcane', archetype: 'orbit', maxLevel: 5, color: 0xbad8ff,
    desc: 'Comet Lance + Moonlet Array: frozen satellites shear through the horde.',
    stats: (l) => mk({ damage: 12 + l * 5, count: 4 + l, radius: 11, range: 82 + l * 5, angularSpeed: 2.9, slowMul: 0.64, slowSec: 0.9 }),
  },
  orbitalStorm: {
    key: 'orbitalStorm', name: 'Orbital Storm', element: 'spark', archetype: 'orbit', maxLevel: 5, color: 0x7eefff,
    desc: 'Arc Relay + Moonlet Array: charged satellites form a rapid electric ring.',
    stats: (l) => mk({ damage: 14 + l * 6, count: 3 + l, radius: 12, range: 92 + l * 5, angularSpeed: 3.3 }),
  },
  prismCataclysm: {
    key: 'prismCataclysm', name: 'Prism Cataclysm', element: 'arcane', archetype: 'lob', maxLevel: 5, color: 0xec78e1,
    desc: 'Prism Scepter + Comet Lance: a ricochet fractures into piercing star shards.', projSprite: 'prismBolt',
    stats: (l) => mk({ damage: 17 + l * 6, cooldownSec: Math.max(0.42, 1.02 - l * 0.06), count: 2 + Math.floor(l / 2), speed: 350, radius: 7, range: 550, lifeSec: 1.5, pierce: 2 + l, terrainBounces: 1, splitCount: 3 + Math.floor(l / 2), splitDamage: 8 + l * 3 }),
  },

  // --- NinjaKid Dojo: six ninja tools and six native reactions (kid-named) ---
  kunai: {
    key: 'kunai', name: 'Kunai Throw', element: 'acid', archetype: 'lob', maxLevel: 8, color: 0xbfc7d5,
    desc: 'Flings a steel kunai at the nearest foe.', projSprite: 'projectileKunai',
    stats: (l) => mk({ damage: 8 + l * 4, cooldownSec: Math.max(0.36, 1.14 - l * 0.07), count: 1 + Math.floor(l / 3), speed: 345, radius: 4 + l * 0.25, range: 480, lifeSec: 1.4 }),
  },
  smokeBomb: {
    key: 'smokeBomb', name: 'Smoke Bomb', element: 'fire', archetype: 'cone', maxLevel: 8, color: 0x9aa0a8,
    desc: 'Bursts a cone of hot smoke pellets that smolder.',
    stats: (l) => mk({ damage: 4 + l * 2, cooldownSec: Math.max(0.5, 1.2 - l * 0.06), count: 4 + l, speed: 295, radius: 5, range: 190, lifeSec: 0.33, coneHalfAngle: 0.52, burnDps: 4 + l * 1.5, burnSec: 2 }),
  },
  canine: {
    key: 'canine', name: 'Canine', element: 'frost', archetype: 'pierce', maxLevel: 8, color: 0xd0559e,
    desc: 'Turn dog! A spectral hound dashes through a line of foes.', projSprite: 'projectileHound',
    stats: (l) => mk({ damage: 9 + l * 3, cooldownSec: Math.max(0.5, 1.28 - l * 0.07), count: 1 + Math.floor(l / 4), speed: 440, radius: 5, range: 530, lifeSec: 1.15, pierce: 1 + l, slowMul: 0.68, slowSec: 0.9 }),
  },
  annoyHim: {
    key: 'annoyHim', name: 'Annoy Him', element: 'spark', archetype: 'chain', maxLevel: 8, color: 0xb07fe8,
    desc: 'A hypnotic zap hops from head to head, annoying everyone.',
    stats: (l) => mk({ damage: 7 + l * 3, cooldownSec: Math.max(0.45, 1.1 - l * 0.06), count: 2 + l, range: 310 }),
  },
  oplorous: {
    key: 'oplorous', name: 'Oplorous', element: 'poison', archetype: 'aura', maxLevel: 8, color: 0xe8c84a,
    desc: 'A confusing golden haze damages and slows all around you.',
    stats: (l) => mk({ damage: 3 + l * 1.5, radius: 70 + l * 8, tickSec: 0.5, slowMul: 0.82, slowSec: 0.6 }),
  },
  distorsiun: {
    key: 'distorsiun', name: 'Distorsiun', element: 'arcane', archetype: 'orbit', maxLevel: 8, color: 0x8ab4ff,
    desc: 'A field orbit: warped blades circle you and smash foes.',
    stats: (l) => mk({ damage: 6 + l * 2.5, count: 2 + Math.floor(l / 2), radius: 9, range: 68 + l * 4, angularSpeed: 2.5 }),
  },
  invisibility: {
    key: 'invisibility', name: 'Invisibility', element: 'frost', archetype: 'lob', maxLevel: 5, color: 0xbfe8f0,
    desc: 'Kunai + Smoke Bomb: unseen blades pierce through the horde.', projSprite: 'projectileKunai',
    stats: (l) => mk({ damage: 16 + l * 6, cooldownSec: Math.max(0.4, 1.0 - l * 0.06), count: 2 + Math.floor(l / 2), speed: 340, radius: 6, range: 520, lifeSec: 1.45, pierce: 2 + l }),
  },
  wolfPack: {
    key: 'wolfPack', name: 'Wolf Pack', element: 'frost', archetype: 'pierce', maxLevel: 5, color: 0xe07ab8,
    desc: 'Kunai + Canine: a pack of spectral hounds charges in a line.', projSprite: 'projectileHound',
    stats: (l) => mk({ damage: 13 + l * 5, cooldownSec: Math.max(0.42, 1.05 - l * 0.06), count: 2 + Math.floor(l / 2), speed: 460, radius: 6, range: 560, lifeSec: 1.2, pierce: 2 + l, slowMul: 0.62, slowSec: 1.0 }),
  },
  sleepySmoke: {
    key: 'sleepySmoke', name: 'Sleepy Smoke', element: 'poison', archetype: 'aura', maxLevel: 5, color: 0xc0cad8,
    desc: 'Smoke Bomb + Oplorous: a numbing cloud that scalds and slows.',
    stats: (l) => mk({ damage: 10 + l * 4, radius: 95 + l * 12, tickSec: 0.4, slowMul: 0.55, slowSec: 1.0, burnDps: 4 + l * 1.5, burnSec: 1.2 }),
  },
  hypnoHalo: {
    key: 'hypnoHalo', name: 'Hypno Halo', element: 'spark', archetype: 'orbit', maxLevel: 5, color: 0xcda8ff,
    desc: 'Annoy Him + Distorsiun: a rapid ring of hypnotic charge.',
    stats: (l) => mk({ damage: 14 + l * 6, count: 3 + l, radius: 12, range: 90 + l * 5, angularSpeed: 3.2 }),
  },
  cursedHowl: {
    key: 'cursedHowl', name: 'Cursed Howl', element: 'spark', archetype: 'chain', maxLevel: 5, color: 0xd6a2ff,
    desc: 'Canine + Annoy Him: a haunted howl leaps far between foes.',
    stats: (l) => mk({ damage: 14 + l * 5, cooldownSec: Math.max(0.4, 0.9 - l * 0.05), count: 4 + l, range: 345 }),
  },
  dizzyVortex: {
    key: 'dizzyVortex', name: 'Dizzy Vortex', element: 'arcane', archetype: 'orbit', maxLevel: 5, color: 0xa5c8ff,
    desc: 'Oplorous + Distorsiun: a spinning ring that leaves foes reeling.',
    stats: (l) => mk({ damage: 12 + l * 5, count: 4 + l, radius: 11, range: 80 + l * 5, angularSpeed: 2.8, slowMul: 0.7, slowSec: 0.8 }),
  },
};

/** A reaction fuses two element weapons into a new one (drafted when both are leveled). */
export interface ReactionRecipe {
  result: string;
  a: string;
  b: string;
}

export const REACTION_RECIPES: ReactionRecipe[] = [
  { result: 'steam', a: 'fire', b: 'frost' },
  { result: 'caustic', a: 'acid', b: 'spark' },
  { result: 'napalm', a: 'fire', b: 'poison' },
  { result: 'shatter', a: 'frost', b: 'arcane' },
  { result: 'storm', a: 'spark', b: 'arcane' },
  { result: 'vitriol', a: 'acid', b: 'frost' },
  { result: 'boilingWort', a: 'cinderHops', b: 'thornNeedle' },
  { result: 'thunderSap', a: 'sapFlask', b: 'fireflyJar' },
  { result: 'smolderMold', a: 'cinderHops', b: 'sporeShroud' },
  { result: 'thornCrown', a: 'thornNeedle', b: 'seedHalo' },
  { result: 'fireflyHalo', a: 'fireflyJar', b: 'seedHalo' },
  { result: 'nectarCatalyst', a: 'sapFlask', b: 'sporeShroud' },
  { result: 'coronaMist', a: 'solarFan', b: 'cometLance' },
  { result: 'auroraArc', a: 'prismScepter', b: 'arcRelay' },
  { result: 'redGiant', a: 'solarFan', b: 'gravityWell' },
  { result: 'cometCrown', a: 'cometLance', b: 'moonletArray' },
  { result: 'orbitalStorm', a: 'arcRelay', b: 'moonletArray' },
  { result: 'prismCataclysm', a: 'prismScepter', b: 'cometLance' },
  { result: 'invisibility', a: 'kunai', b: 'smokeBomb' },
  { result: 'wolfPack', a: 'kunai', b: 'canine' },
  { result: 'sleepySmoke', a: 'smokeBomb', b: 'oplorous' },
  { result: 'hypnoHalo', a: 'annoyHim', b: 'distorsiun' },
  { result: 'cursedHowl', a: 'canine', b: 'annoyHim' },
  { result: 'dizzyVortex', a: 'oplorous', b: 'distorsiun' },
];

/** Minimum level of BOTH ingredients before a reaction can be drafted. */
export const REACTION_REQ = 5;

// --- Projectiles ---
export interface Projectile {
  x: number;
  y: number;
  prevX: number;
  prevY: number;
  vx: number;
  vy: number;
  radius: number;
  damage: number;
  lifeSec: number;
  color: number;
  pierceLeft: number;
  id: number; // unique per spawn; enemies record it to avoid double-hits
  slowMul: number;
  slowSec: number;
  burnDps: number;
  burnSec: number;
  burstDamage: number;
  burstRadius: number;
  terrainBounces: number;
  splitCount: number;
  splitDamage: number;
  bounceChainCount: number;
  bounceChainDamage: number;
  bounceChainRange: number;
  /** Briefly retained after a rail carries the projectile, so a bounce that follows
   * immediately receives Aurora Arc's longer next jump. */
  railChargedSec: number;
  sprite: string; // texture key (default 'proj'; hidden fire uses 'paper')
}

const makeProjectile = (): Projectile => ({
  x: 0,
  y: 0,
  prevX: 0,
  prevY: 0,
  vx: 0,
  vy: 0,
  radius: 1,
  damage: 0,
  lifeSec: 0,
  color: 0xffffff,
  pierceLeft: 0,
  id: 0,
  slowMul: 1,
  slowSec: 0,
  burnDps: 0,
  burnSec: 0,
  burstDamage: 0,
  burstRadius: 0,
  terrainBounces: 0,
  splitCount: 0,
  splitDamage: 0,
  bounceChainCount: 0,
  bounceChainDamage: 0,
  bounceChainRange: 0,
  railChargedSec: 0,
  sprite: 'proj',
});

export const createProjectilePool = (): Pool<Projectile> =>
  new Pool(POOL_CAP.projectiles, makeProjectile);

interface WeaponInstance {
  def: WeaponDef;
  level: number;
  timer: number;
  stats: WeaponStats; // cached resolved stats for `level`
}

const ORBIT_HIT_CD = 0.22;
const LOB_SPREAD = 0.18;

export class WeaponSystem {
  private readonly instances: WeaponInstance[] = [];
  private readonly byKey = new Map<string, WeaponInstance>();
  private readonly buf: number[] = [];
  private orbitAngle = 0;
  private chainToken = 0;
  private nextProjId = 1;
  // Targeting viewport half-extents (world px) - weapons never target off-screen.
  private viewHalfW: number = VIEW.defaultHalfW;
  private viewHalfH: number = VIEW.defaultHalfH;

  // Visual state for the renderer (read-only from outside).
  orbitActive = false;
  orbitCount = 0;
  orbitRingRadius = 0;
  orbitOrbRadius = 0;
  orbitColor = ELEMENT_COLOR.arcane;
  orbitSpin = 0;
  auraActive = false;
  auraRadius = 0;
  auraColor = ELEMENT_COLOR.poison;
  auraWeaponKey = '';
  readonly orbitVisuals: OrbitVisual[] = [];

  get ownedCount(): number {
    return this.instances.length;
  }

  has(key: string): boolean {
    return this.byKey.has(key);
  }

  getLevel(key: string): number {
    return this.byKey.get(key)?.level ?? 0;
  }

  ownedKeys(): string[] {
    return this.instances.map((i) => i.def.key);
  }

  /** Highest per-hit damage across owned weapons (x the passive damage mult). Used by the
   *  hidden variant's abilities that scale off "your regular damage". 0 if none owned. */
  maxHitDamage(damageMul: number): number {
    let best = 0;
    for (const inst of this.instances) {
      const d = inst.stats.damage * damageMul;
      if (d > best) best = d;
    }
    return best;
  }

  add(key: string): void {
    if (this.byKey.has(key)) {
      this.levelUp(key);
      return;
    }
    const def = WEAPON_DEFS[key];
    const inst: WeaponInstance = { def, level: 1, timer: 0, stats: def.stats(1) };
    this.instances.push(inst);
    this.byKey.set(key, inst);
  }

  levelUp(key: string): void {
    const inst = this.byKey.get(key);
    if (!inst || inst.level >= inst.def.maxLevel) return;
    inst.level++;
    inst.stats = inst.def.stats(inst.level);
  }

  update(
    dt: number,
    px: number,
    py: number,
    projectiles: Pool<Projectile>,
    ctx: HitCtx,
    viewHalfW: number,
    viewHalfH: number,
  ): void {
    this.viewHalfW = viewHalfW;
    this.viewHalfH = viewHalfH;
    this.orbitActive = false;
    this.auraActive = false;
    this.auraWeaponKey = '';
    this.orbitVisuals.length = 0;
    this.orbitAngle += dt; // base spin; per-orbit speed applied at render/use time

    for (let i = 0; i < this.instances.length; i++) {
      const inst = this.instances[i];
      const s = inst.stats;
      const mods = ctx.mods;
      switch (inst.def.archetype) {
        case 'lob':
        case 'pierce':
          this.fireProjectiles(dt, inst, px, py, projectiles, ctx);
          break;
        case 'cone':
          this.fireCone(dt, inst, px, py, projectiles, ctx);
          break;
        case 'chain':
          this.fireChain(dt, inst, px, py, ctx);
          break;
        case 'aura':
          this.tickAura(dt, inst, px, py, ctx);
          this.auraActive = true;
          this.auraRadius = s.radius * mods.areaMul;
          this.auraColor = defColor(inst.def);
          this.auraWeaponKey = inst.def.key;
          break;
        case 'orbit':
          this.tickOrbit(inst, px, py, ctx);
          this.orbitActive = true;
          this.orbitCount = s.count + mods.countAdd;
          this.orbitRingRadius = s.range * mods.areaMul;
          this.orbitOrbRadius = s.radius * mods.areaMul;
          this.orbitColor = defColor(inst.def);
          this.orbitVisuals.push({
            weaponKey: inst.def.key,
            count: this.orbitCount,
            ringRadius: this.orbitRingRadius,
            orbRadius: this.orbitOrbRadius,
            color: this.orbitColor,
            spin: this.orbitSpin,
          });
          break;
      }
    }
  }

  private nearest(px: number, py: number, range: number, ctx: HitCtx, skipToken = -1): number {
    const n = ctx.hash.query(px, py, range, this.buf);
    let best = -1;
    let bd = range * range;
    const vw = this.viewHalfW;
    const vh = this.viewHalfH;
    for (let i = 0; i < n; i++) {
      const idx = this.buf[i];
      const e = ctx.enemies.items[idx];
      if (e.hp <= 0) continue;
      if (skipToken >= 0 && e.chainMark === skipToken) continue;
      const dx = e.x - px;
      const dy = e.y - py;
      if (dx < -vw || dx > vw || dy < -vh || dy > vh) continue; // off-screen - don't target
      const d2 = dx * dx + dy * dy;
      if (d2 < bd) {
        bd = d2;
        best = idx;
      }
    }
    return best;
  }

  private spawn(
    projectiles: Pool<Projectile>,
    x: number,
    y: number,
    ang: number,
    s: WeaponStats,
    mods: HitCtx['mods'],
    color: number,
    sprite: string,
  ): void {
    const p = projectiles.acquire();
    if (p === null) return;
    p.x = x;
    p.y = y;
    p.prevX = x;
    p.prevY = y;
    p.vx = Math.cos(ang) * s.speed;
    p.vy = Math.sin(ang) * s.speed;
    p.radius = s.radius * mods.areaMul;
    p.damage = s.damage * mods.damageMul;
    p.lifeSec = s.lifeSec;
    p.color = color;
    p.pierceLeft = s.pierce;
    p.id = this.nextProjId++;
    p.slowMul = s.slowMul;
    p.slowSec = s.slowSec;
    p.burnDps = s.burnDps;
    p.burnSec = s.burnSec;
    p.burstDamage = s.burstDamage * mods.damageMul;
    p.burstRadius = s.burstRadius * mods.areaMul;
    p.terrainBounces = s.terrainBounces;
    p.splitCount = s.splitCount;
    p.splitDamage = s.splitDamage * mods.damageMul;
    p.bounceChainCount = s.bounceChainCount + mods.countAdd;
    p.bounceChainDamage = s.bounceChainDamage * mods.damageMul;
    p.bounceChainRange = s.bounceChainRange * mods.areaMul;
    p.railChargedSec = 0;
    p.sprite = sprite;
  }

  private fireProjectiles(
    dt: number,
    inst: WeaponInstance,
    px: number,
    py: number,
    projectiles: Pool<Projectile>,
    ctx: HitCtx,
  ): void {
    inst.timer -= dt;
    if (inst.timer > 0) return;
    const s = inst.stats;
    const mods = ctx.mods;
    const target = this.nearest(px, py, s.range * mods.areaMul, ctx);
    if (target < 0) return;
    const e = ctx.enemies.items[target];
    const aim = Math.atan2(e.y - py, e.x - px);
    const count = s.count + mods.countAdd;
    const color = defColor(inst.def);
    const sprite = inst.def.projSprite ?? 'proj';
    for (let k = 0; k < count; k++) {
      const off = count > 1 ? (k / (count - 1) - 0.5) * LOB_SPREAD * count : 0;
      this.spawn(projectiles, px, py, aim + off, s, mods, color, sprite);
    }
    inst.timer = s.cooldownSec * mods.cooldownMul;
  }

  private fireCone(
    dt: number,
    inst: WeaponInstance,
    px: number,
    py: number,
    projectiles: Pool<Projectile>,
    ctx: HitCtx,
  ): void {
    inst.timer -= dt;
    if (inst.timer > 0) return;
    const s = inst.stats;
    const mods = ctx.mods;
    const target = this.nearest(px, py, s.range * mods.areaMul, ctx);
    if (target < 0) return;
    const e = ctx.enemies.items[target];
    const aim = Math.atan2(e.y - py, e.x - px);
    const count = s.count + mods.countAdd;
    const color = defColor(inst.def);
    const sprite = inst.def.projSprite ?? 'proj';
    for (let k = 0; k < count; k++) {
      const off = count > 1 ? (k / (count - 1) - 0.5) * 2 * s.coneHalfAngle : 0;
      this.spawn(projectiles, px, py, aim + off, s, mods, color, sprite);
    }
    inst.timer = s.cooldownSec * mods.cooldownMul;
  }

  private fireChain(dt: number, inst: WeaponInstance, px: number, py: number, ctx: HitCtx): void {
    inst.timer -= dt;
    if (inst.timer > 0) return;
    const s = inst.stats;
    const mods = ctx.mods;
    const range = s.range * mods.areaMul;
    let idx = this.nearest(px, py, range, ctx);
    if (idx < 0) return;
    const token = ++this.chainToken;
    const dmg = s.damage * mods.damageMul;
    const links = s.count + mods.countAdd;
    const color = defColor(inst.def);
    let cx = px;
    let cy = py;
    for (let k = 0; k < links && idx >= 0; k++) {
      const e = ctx.enemies.items[idx];
      e.chainMark = token;
      const beamKind =
        inst.def.key === 'fireflyJar' || inst.def.key === 'thunderSap'
          ? 'firefly'
          : inst.def.key === 'arcRelay'
            ? 'lightning'
            : 'lightning';
      spawnBeam(ctx.beams, cx, cy, e.x, e.y, color, beamKind);
      dealDamage(ctx, e, dmg, e.x, e.y, true);
      cx = e.x;
      cy = e.y;
      idx = this.nearest(cx, cy, range, ctx, token);
    }
    inst.timer = s.cooldownSec * mods.cooldownMul;
  }

  private tickAura(dt: number, inst: WeaponInstance, px: number, py: number, ctx: HitCtx): void {
    inst.timer -= dt;
    if (inst.timer > 0) return;
    const s = inst.stats;
    const mods = ctx.mods;
    const radius = s.radius * mods.areaMul;
    const dmg = s.damage * mods.damageMul;
    const n = ctx.hash.query(px, py, radius + MAX_ENEMY_RADIUS, this.buf);
    for (let i = 0; i < n; i++) {
      const e = ctx.enemies.items[this.buf[i]];
      if (e.hp <= 0) continue;
      const dx = e.x - px;
      const dy = e.y - py;
      const rr = radius + e.radius;
      if (dx * dx + dy * dy <= rr * rr) {
        dealDamage(ctx, e, dmg, e.x, e.y, false);
        applyStatus(e, s);
      }
    }
    inst.timer = s.tickSec;
  }

  private tickOrbit(inst: WeaponInstance, px: number, py: number, ctx: HitCtx): void {
    const s = inst.stats;
    const mods = ctx.mods;
    const count = s.count + mods.countAdd;
    const ring = s.range * mods.areaMul;
    const orbR = s.radius * mods.areaMul;
    const dmg = s.damage * mods.damageMul;
    const spin = this.orbitAngle * s.angularSpeed;
    this.orbitSpin = spin;
    for (let k = 0; k < count; k++) {
      const a = spin + (k / count) * Math.PI * 2;
      const ox = px + Math.cos(a) * ring;
      const oy = py + Math.sin(a) * ring;
      const n = ctx.hash.query(ox, oy, orbR + MAX_ENEMY_RADIUS, this.buf);
      for (let i = 0; i < n; i++) {
        const e = ctx.enemies.items[this.buf[i]];
        if (e.hp <= 0 || e.orbitHitCd > 0) continue;
        const dx = e.x - ox;
        const dy = e.y - oy;
        const rr = orbR + e.radius;
        if (dx * dx + dy * dy <= rr * rr) {
          dealDamage(ctx, e, dmg, e.x, e.y, true);
          applyStatus(e, s);
          e.orbitHitCd = ORBIT_HIT_CD;
        }
      }
    }
  }
}
